import { DocumentNode, useMutation } from '@apollo/client';
import {
  BottomNavigation,
  Button,
  Controller,
  ControllerRenderProps,
  FileElement,
  useForm,
} from '@otp/ui';
import { useTranslation } from 'react-i18next';
import { AttachmentsField } from 'src/components/common/resources/form/attachments-field';
import React, { useEffect, useRef, useState } from 'react';
import { PartialResourcePage, ResourceForm as Form } from './types';
import {
  mapFileFormToResource,
  mapResourceToForm,
} from 'src/components/resource/form/utils';
import {
  CreateResourceDocument,
  ResourcePageType,
  UpdateResourceDocument,
} from 'src/graphql/generated/graphql';
import { ContentWrapper } from 'src/components/common/wrapper/content/wrapper';
import { TableWrapper } from 'src/components/common/wrapper/table/wrapper';
import { FileVersionCommentDialog } from 'src/components/resource/form/comment-dialog';
import { PermissionField } from './fields/permission';
import { GeneralInfoField } from './fields/general-info';
import { ResourceUploaderContent } from './resource-uploader-content';

type DocumentFormProps = {
  type: ResourcePageType.Document | ResourcePageType.Download;
  resource?: PartialResourcePage;
  refetchQueries?: DocumentNode[];
  onCancel: () => void;
  onSave: () => void;
};

export const DocumentForm = ({
  type,
  resource,
  refetchQueries = [],
  onCancel,
  onSave: onSaveProp,
}: DocumentFormProps) => {
  const { t } = useTranslation();
  const isFileUpdated = useRef<boolean>(false);
  const formDataToSubmit = useRef<Form>();

  const [showCommentDialog, setShowCommentDialog] = useState(false);

  const [createResource, { loading: loadingCreate }] = useMutation(
    CreateResourceDocument,
  );
  const [updateResource, { loading: loadingUpdate }] = useMutation(
    UpdateResourceDocument,
  );
  const loading = loadingCreate || loadingUpdate;

  const {
    control,
    reset,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<Form>({
    defaultValues: resource ? mapResourceToForm(resource) : { type },
  });

  useEffect(() => {
    reset(resource ? mapResourceToForm(resource) : { type });
  }, [resource]);

  const onFileUpdate = (
    field: ControllerRenderProps<Form, 'fileAsset'>,
    files: FileElement[],
  ) => {
    isFileUpdated.current = true;

    return setValue(field.name, files?.[0], {
      shouldDirty: true,
    });
  };

  const onSubmit = async (data: Form) => {
    formDataToSubmit.current = data;

    // If user uploaded a new file and this is update form, we need to ask about comment
    if (isFileUpdated.current && resource?.id) {
      setShowCommentDialog(true);
      return;
    }

    onSave();
  };

  const onSave = async (extraData: { comment?: string } = {}) => {
    if (!formDataToSubmit.current) {
      return;
    }

    const { comment } = extraData;

    const formData = mapFileFormToResource(
      {
        ...(formDataToSubmit.current as Form),
        comment,
      },
      resource,
    );

    if (resource?.id) {
      await updateResource({
        variables: {
          id: resource.id,
          data: {
            ...formData,
          },
        },
        refetchQueries,
      });
    } else {
      await createResource({
        variables: {
          data: formData,
        },
        refetchQueries,
      });
    }

    onSaveProp();
  };

  return (
    <ContentWrapper>
      <form onSubmit={handleSubmit(onSubmit)}>
        <TableWrapper className="mt-6">
          <div className="mb-4">
            <Controller
              name="fileAsset"
              control={control}
              rules={{ required: true }}
              render={({ field }) => (
                <div>
                  <AttachmentsField
                    error={!!errors.fileAsset}
                    value={field.value ? [field.value] : []}
                    onChange={(files) => onFileUpdate(field, files)}
                    reverseDirection={true}
                    buttonContent={() => (
                      <ResourceUploaderContent type={type} />
                    )}
                    buttonFullWidth={true}
                    fileDescription={false}
                    fileAccept={
                      type === ResourcePageType.Document
                        ? ['application/pdf']
                        : undefined
                    }
                    maxFileCount={1}
                    showLoader={false}
                  />
                </div>
              )}
            />
          </div>

          <GeneralInfoField control={control} errors={errors} />
        </TableWrapper>

        <TableWrapper className="mt-6">
          <PermissionField control={control} />

          <BottomNavigation>
            <Button
              className="mr-2"
              type="submit"
              variant="contained"
              color="primary"
              size="medium"
              disabled={loading}
            >
              {t('common.save')}
            </Button>
            <Button
              variant="outlined"
              color="primary"
              onClick={onCancel}
              className="mr-4"
            >
              {t('common.close')}
            </Button>
          </BottomNavigation>
        </TableWrapper>
      </form>
      {showCommentDialog && (
        <FileVersionCommentDialog
          onSubmit={(...data) => {
            onSave(...data);
            setShowCommentDialog(false);
          }}
          onClose={() => setShowCommentDialog(false)}
        />
      )}
    </ContentWrapper>
  );
};
